/* eslint-disable react-hooks/exhaustive-deps */
import { useNavigate } from "react-router";
import {
  ControlledInput,
  ControlledSelect,
  PrimaryButton,
  SecondaryButton,
} from "../../components";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { useForm } from "react-hook-form";
import { emailValidation } from "../../utils/regexValidations";
import axios from "axios";
import { useAlert } from "../../store/useAlert";
import { useAppLoading } from "../../store/useAppLoading";
import { useEffect } from "react";
import { useDropLocations } from "../../store/useDropLocations";

type Inputs = {
  first_name: string;
  last_name?: string;
  email: string;
  phone_number?: string;
  user_role?: "1" | "2" | "3" | "4" | "5" | "6";
  password: string; // Still required in type for API compatibility
  defaultLocation?: string;
};

function AddUserScreen() {
  /** Hooks */
  const { getDropLocations, dropLocations } = useDropLocations();
  const navigate = useNavigate();
  const goBack = () => navigate(-1);
  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      email: "",
      first_name: "",
      last_name: "",
      phone_number: "",
      user_role: "3",
      password: "",
      defaultLocation: "",
    },
  });
  const { setAlert } = useAlert();
  const { setIsLoading } = useAppLoading();

  useEffect(() => {
    getDropLocations();
  }, []);

  useEffect(() => {
    if (watch("first_name") || watch("last_name")) {
      setValue(
        "email",
        `${userRoles.find((role) => role._id === watch("user_role"))?.title?.toLowerCase() ?? "admin"}_${watch("first_name").toLowerCase()}${watch("last_name") ? "." : ""}${watch("last_name")?.toLowerCase()}@internal.local`
      );
    }
  }, [watch("first_name"), watch("last_name"), watch("user_role")]);

  const onSubmit = async (data: Inputs) => {
    setIsLoading(true);
    try {
      const registerBody: Inputs = {
        first_name: data.first_name,
        email: data.email,
        // Only include password if it's provided or if user role is Store Admin (5)
        password: data.user_role === "5" ? data.password : data.password || "",
        // @ts-ignore
        user_role: Number(data.user_role),
        defaultLocation: data?.defaultLocation ?? "",
      };
      if (data.phone_number) {
        registerBody.phone_number = data.phone_number;
      }
      if (data.last_name) {
        registerBody.last_name = data.last_name;
      }
      const registerResponse = await axios.post("signup/admin", registerBody);
      if (registerResponse?.data?.accessToken) {
        goBack();
      } else if (registerResponse?.data?.message) {
        setAlert({
          isOpen: true,
          message: registerResponse?.data?.message,
          severity: "error",
        });
      }
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error?.data?.message || "An error occurred",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-row items-center mb-6">
        <h1 className="font-satoshi text-2xl font-bold text-dark">
          Add new user
        </h1>
      </div>
      <div className="w-full sm:w-1/2 bg-white  border-gray-border drop-shadow-none rounded-xl">
        <div className="w-full border-b border-gray-border px-[28px] py-[15px]">
          <p className="font-satoshi font-medium text-base text-dark">
            New user
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full px-7 py-7">
          {/* First name */}
          <ControlledInput
            control={control}
            error={Boolean(errors?.first_name)}
            label="First name"
            placeholder="Enter first name"
            name="first_name"
            rules={{ required: true }}
          />
          {/* Last name */}
          <ControlledInput
            control={control}
            error={Boolean(errors?.last_name)}
            label="Last name"
            placeholder="Enter last name"
            name="last_name"
            rules={{ required: true }}
          />
          {/* Email */}
          <ControlledInput
            control={control}
            error={Boolean(errors?.email)}
            label="Email"
            placeholder="Enter e-mail"
            name="email"
            rules={{ required: true, pattern: emailValidation }}
          />
          {/* Phone */}
          <ControlledInput
            control={control}
            error={Boolean(errors?.phone_number)}
            label="Phone Number"
            placeholder="Enter phone number"
            name="phone_number"
            type="tel"
            rules={{ required: false }}
          />
          <ControlledSelect
            name="defaultLocation"
            control={control}
            rules={{ required: true }}
            error={Boolean(errors?.defaultLocation)}
            label="Default drop location"
            options={dropLocations?.map((location) => ({
              _id: location._id as string,
              title: location?.cost_place as string,
            }))}
            placeholder="Pick default drop location here"
          />
          <ControlledSelect
            name="user_role"
            control={control}
            rules={{ required: true }}
            error={Boolean(errors?.user_role)}
            label="User role"
            options={userRoles}
            placeholder="Pick user role here"
          />
          {/* Password */}
          <ControlledInput
            control={control}
            error={Boolean(errors?.password)}
            label="Password"
            type="password"
            placeholder="Enter password"
            name="password"
            rules={{
              required: watch("user_role") === "5",
              validate: (value) =>
                watch("user_role") === "5" ? value.length >= 6 : true,
            }}
          />
          <div className="flex flex-row items-center justify-end">
            <SecondaryButton
              type="button"
              onClick={goBack}
              label="Cancel"
              className="mr-3 h-8 px-4 py-0  border border-droppie-oranje  text-droppie-oranje hover:bg-droppie-oranje/5 flex items-center justify-center"
              labelClassName="text-droppie-oranje"
            />
            <PrimaryButton
              type="submit"
              className="ml-4 !bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              size="sm"
              label="Save"
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default withProtectedRoute(AddUserScreen);

const userRoles = [
  { _id: "1", title: "Admin" },
  { _id: "5", title: "Store Admin" },
  { _id: "2", title: "Manager" },
  { _id: "3", title: "User" },
  { _id: "4", title: "External Viewer" },
  { _id: "6", title: "Plastic Viewer" },
];
